import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import moment from 'moment';
import {useCustomersUIContext} from '../CustomersUIContext';
import ButtonExportToExcel from '../../../_common/components/ButtonExportToExcel';
import FilterTextField from '../../../_common/components/FilterTextField';
import FilterSelect from "../../../_common/components/FilterSelect";
import FiltersBar from '../../../_common/components/FiltersBar';
import * as actions from '../../_redux/customersActions';
import {headersCustomers} from '../../../_common/constants/exportedFilesHeaders';
import {customersHeader} from '../../../_common/constants/exportedFilesMainHeaders';
import {generateExcelFile} from '../../../_common/utils/excel';
import {formatPhoneNumberForExcel} from '../../../../../utils';
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';

const prepareFilter = (queryParams, values) => {
  const {customerID, customerName, customerIDNumber, email, allowInvoiceDeal} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(customerID)) {
    filter.customerID = customerID;
  } else {
    delete newQueryParams.customerID;
  }
  if (Boolean(customerName)) {
    filter.customerName = customerName;
  } else {
    delete newQueryParams.customerName;
  }
  if (Boolean(customerIDNumber)) {
    filter.customerIDNumber = customerIDNumber;
  } else {
    delete newQueryParams.customerIDNumber;
  }
  if (Boolean(email)) {
    filter.email = email;
  } else {
    delete newQueryParams.email;
  }
  if (allowInvoiceDeal) {
    newQueryParams.allowInvoiceDeal = allowInvoiceDeal;
  } else {
    delete newQueryParams.allowInvoiceDeal;
  }

  return {...newQueryParams, ...filter};
};

export function CustomersFilter() {
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();
  const [loading, setLoading] = useState(false);
  const customersUIContext = useCustomersUIContext();
  const customersUIProps = useMemo(() => {
    return {
      queryParams: customersUIContext.queryParams,
      setQueryParams: customersUIContext.setQueryParams,
    };
  }, [customersUIContext]);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getCustomersListIDPrefix);
    const newQueryParams = prepareFilter(customersUIProps.queryParams, values);
    if (!isEqual(newQueryParams, customersUIProps.queryParams)) {
      customersUIProps.setQueryParams(newQueryParams);
    }
  };

  const qs = new URLSearchParams(window.location.search);
  const customerID = qs.get('customerID') || '';
  const customerName = qs.get('customerName') || '';
  const customerIDNumber = qs.get('customerIDNumber') || '';
  const email = qs.get('email') || '';
  const allowInvoiceDeal = qs.get('allowInvoiceDeal') || '';

  const exportCustomersFileFunc = () => {
    setLoading(true);
    dispatch(actions.exportCustomersFile(customersUIProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(data, `${moment().format('DD-MM-YYYY')} - לקוחות.xlsx`, customersHeader);
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false))
    ;
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        _id,
        fullName,
        contactName,
        idNumber,
        address,
        phone,
        email,
        totalAmount,
        allowInvoiceDeal
      } = item;

      return {
        [headersCustomers[0]]: index + 1,
        [headersCustomers[1]]: _id,
        [headersCustomers[2]]: fullName,
        ...(fullName !== contactName && contactName
          ? {[headersCustomers[3]]: contactName}
          : {}),
        [headersCustomers[4]]: idNumber || '',
        [headersCustomers[5]]: address,
        [headersCustomers[6]]: formatPhoneNumberForExcel(phone),
        [headersCustomers[7]]: email,
        [headersCustomers[8]]: totalAmount,
        [headersCustomers[9]]: allowInvoiceDeal ? 'קיים' : 'לא קיים',
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{
        customerID,
        customerName,
        customerIDNumber,
        email,
        allowInvoiceDeal
      }}
      onSubmit={(values) => applyFilter(values)}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
        <FiltersBar
          filters={
            <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
              <FilterTextField
                fieldName="customerID"
                placeholder={formatMessage({id: 'customers-filter-customerID', defaultMessage: 'Customer ID'})}
                value={values.customerID}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="customerName"
                placeholder={formatMessage({id: 'customers-filter-customerName', defaultMessage: 'Customer name'})}
                value={values.customerName}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                fieldName="customerIDNumber"
                placeholder={formatMessage({id: 'customers-filter-idNumber', defaultMessage: 'ID number'})}
                value={values.customerIDNumber}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterTextField
                type={'email'}
                fieldName="email"
                placeholder={formatMessage({id: 'customers-filter-email', defaultMessage: 'Email'})}
                value={values.email}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterSelect
                fieldName={'allowInvoiceDeal'}
                valueName={values.allowInvoiceDeal}
                placeholder={formatMessage({id: 'customers-filter-allowInvoiceDeal', defaultMessage: 'Allow invoice deal'})}
                containerStyles={containerClassnames}
                options={[
                  {label: 'כן לאפשר', value: 'true'},
                  {label: 'לא לאפשר', value: 'false'},
                ]}
                {...formikProps}
              />
            </div>
          }
          buttons={
            <ButtonExportToExcel
              loading={loading}
              exportFunc={exportCustomersFileFunc}
            />
          }
        />
      )}
    </Formik>
  );
}